import { debounce, makeStyles, Paper, Typography } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { SwipeEventData, useSwipeable } from 'react-swipeable'
import { useGlobalState } from '../../providers/GlobalState'
import { IolLibrary } from '../../types/graphql'
import NumberFormat from 'react-number-format'
import { useEditMode } from '../../providers/EditMode'

const useStyles = makeStyles(theme => ({
  root: {
    '&.MuiPaper-root': {
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6))',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      margin: '10px 0px',
      padding: 10,
      height: 80,
      scrollSnapAlign: 'start',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'left'
    }
  },
  disabled: {
    '&.MuiPaper-root': {
      background: '#f89c9c'
    }
  },
  active: {
    '&.MuiPaper-root': {
      background:
        'linear-gradient(0deg, rgba(70, 164, 224, 0.6), rgba(70, 164, 224, 0.6))',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center'
    }
  },
  text: {
    '&.MuiTypography-root': {
      whiteSpace: 'break-spaces',
      textTransform: 'capitalize'
    }
  },
  list: {
    height: '80vh',
    overflowX: 'hidden',
    overflowY: 'scroll',
    position: 'relative',
    scrollSnapType: 'y proximity',
    cursor: 'pointer',
    paddingBottom: '20vh',
    touchAction: 'none',
    /* width */
    '&::-webkit-scrollbar': {
      width: 12
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
      borderRadius: theme.shape.borderRadius
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.primary.dark
    }
  }
}))
export declare const LEFT = 'Left'
export declare const RIGHT = 'Right'
export declare const UP = 'Up'
export declare const DOWN = 'Down'
export declare type SwipeDirections =
  | typeof LEFT
  | typeof RIGHT
  | typeof UP
  | typeof DOWN
const IolLib = (): JSX.Element => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [index, setIndex] = useState(0)
  const listEl = useRef<HTMLDivElement>(null)
  const { iolLibraries } = useGlobalState()
  const { editMode } = useEditMode()

  const handleScroll = (i: number) => {
    const value = 90 * i
    listEl.current?.scrollTo({
      top: value,
      behavior: 'smooth'
    })
  }

  const debouncedScroll = debounce(handleScroll, 10)

  useEffect(() => {
    debouncedScroll(index)

    return () => {
      debouncedScroll(index)
    }
  }, [index])

  const handleChangeIndexUp = () => {
    // array lenght
    if (index >= iolLibraries.length) {
      history.push(`/nav/biblioteca/${iolLibraries[iolLibraries.length]?.slug}`)
      setIndex(iolLibraries.length)
    } else {
      history.push(`/nav/biblioteca/${iolLibraries[index + 1]?.slug}`)
      setIndex(index + 1)
    }
  }

  const handleChangeIndexDown = () => {
    if (index <= 0) {
      history.push(`/nav/biblioteca/${iolLibraries[0]?.slug}`)
      setIndex(0)
    } else {
      history.push(`/nav/biblioteca/${iolLibraries[index - 1]?.slug}`)
      setIndex(index - 1)
    }
  }

  const debouncedChangeUp = debounce(handleChangeIndexUp, 10)
  const debouncedChangeDown = debounce(handleChangeIndexDown, 10)

  const handlers = useSwipeable({
    onSwiping: (eventData: SwipeEventData) => {
      if (eventData.dir === 'Up') {
        debouncedChangeUp()
      } else if (eventData.dir === 'Down') {
        debouncedChangeDown()
      }
    }
  })

  const getIolPrice = (iol: IolLibrary | null) => {
    if (!iol) return null
    if (!iol?.price?.float) return null

    return (
      <Typography
        variant="subtitle1"
        color="textPrimary"
        className={classes.text}
      >
        <NumberFormat
          prefix="R$ "
          isNumericString
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          fixedDecimalScale
          displayType="text"
          value={iol?.price?.float}
        />{' '}
      </Typography>
    )
  }

  const shouldShowIol = (iol: IolLibrary | null): boolean => {
    if (editMode) return true
    return iol?.enable || false
  }

  return (
    <div className={clsx(classes.list)} ref={listEl}>
      <div {...handlers}>
        {[...(iolLibraries || [])]
          ?.sort((a, b) => ((a?.position || 0) > (b?.position || 0) ? 1 : -1))
          .filter(shouldShowIol)
          .map((iol, index) => (
            <Paper
              key={iol?.slug}
              square
              elevation={0}
              onClick={() => {
                setIndex(index)
                history.push(`/nav/biblioteca/${iol?.slug}`)
              }}
              className={clsx(classes.root, {
                [classes.active]:
                  location.pathname === `/nav/biblioteca/${iol?.slug}`,
                [classes.disabled]: !iol?.enable
              })}
            >
              <Typography
                variant="subtitle2"
                color="textSecondary"
                className={classes.text}
              >
                {iol?.name}
              </Typography>
              {getIolPrice(iol)}
            </Paper>
          ))}
      </div>
    </div>
  )
}

export default IolLib
